import { definitions } from '@/types/api';
import axios from '@/axios/api';
import {
  onMounted, reactive, Ref, ref,
} from 'vue';
import { MessageApi } from 'ant-design-vue/lib/message';
import { message, Modal } from 'ant-design-vue';
import validate from '@/lib/validate';
import {
  CreateI, DeleteI, QueryI, UpdateI,
} from '@/interface/basicOperations';
import { BasicUser } from '@/interface/basicUser';
import CommonStore from '@/store/common/index';
// eslint-disable-next-line @typescript-eslint/camelcase
import { export_json_to_excel } from '@/lib/Export2Excel';
import userServices from '@/services/user.services';
import { promiseConfirm } from '@/lib/message';
import TeacherModuleFun from './teacherModule';
// eslint-disable-next-line import/no-cycle
import { ClassMember } from './classMemberModule';
import {
  FileItem, ClassInfoI, ClassCreateRequestI, ClassUpdateRequestI, ClassDeleteRequestI, ClassEditFormI,
} from './interface';

const { delClassById } = userServices();

/**
 * 服务器查询班级列表
 * @param reqData 查询参数
 * @returns
 */
const queryClassList = async (reqData: definitions['QueryClassRequest']): Promise<definitions['Page«Class»']> => {
  const response = await axios.getClassList(reqData);
  const { data } = response.data;
  return data;
};

/**
 * 获取服务器获取后的班级列表
 * @param reqData 查询参数
 * @returns
 */
const getClassList = async (reqData: definitions['QueryClassRequest']) => {
  const { list, totalRecords } = await queryClassList(reqData);
  const classList = list?.map((item) => ({
    id: item.id || '',
    name: item.name || '',
    stuNum: item.stuNum || 0,
    teacherCodes: item.teacherCodes || '',
    teacherName: item.userNames || '',
  }));

  return {
    classList: classList || [],
    totalRecords,
  };
};

/**
 * 服务器新增、修改班级信息
 * @param reqData 接口参数
 * @returns
 */
const editClassData = async (reqData: definitions['MergeClassRequest']): Promise<definitions['Class']> => {
  // reqData.teacherCodes = JSON.stringify([reqData.teacherCodes]); // 引用类型，对reqData做了修改，导致页面绑定数据也有变化
  const response = await axios.editClassData({ ...reqData, teacherCodes: JSON.stringify([reqData.teacherCodes]) });
  const { data } = response;
  return data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatJson = (filterVal: any[], jsonData: any[]) => jsonData.map((v: { [x: string]: any }) => filterVal.map((j: string | number) => v[j]));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const exportErrorExcel = (errList: any[]) => new Promise((resolve, reject) => {
  try {
    const tHeader = ['用户学号', '用户姓名', '异常信息'];
    const filterVal = ['code', 'name', 'message'];
    const data = formatJson(filterVal, errList);
    export_json_to_excel({
      header: tHeader,
      data,
      filename: '异常账号名单',
      bookType: 'xls',
    });
    resolve(true);
  } catch (error) {
    reject(error);
  }
});

export interface ClassI extends QueryI<unknown, ClassInfoI[]>, CreateI<ClassCreateRequestI, ClassInfoI>, UpdateI<ClassUpdateRequestI, ClassInfoI>, DeleteI<ClassDeleteRequestI, boolean> {
  /**
   * 班级列表
   */
  list: ClassInfoI[];
  /**
   * 所有教师列表
   */
  teacherList: BasicUser[];
  /**
   * 班级总数
   */
  totalRecords: number;
  /**
   * 班级导入学生
   */
  importStudent: (formData: FormData, classId: string) => Promise<boolean | string[]>;
  importStudentDamocles: (formData: FormData, classId: string) => Promise<boolean | string[]>;
}

export class Class implements ClassI {
  totalRecords = 0;

  list: ClassInfoI[] = [];

  teacherList: BasicUser[] = []

  // eslint-disable-next-line class-methods-use-this
  async importStudent(formData: FormData, classId: string): Promise<boolean | string[]> {
    try {
      const { data } = await (await axios.uploadStudentByClass(formData, classId)).data;
      if (Array.isArray(data.errorMessage) && data.errorMessage.length > 0) {
        return data.errorMessage as string[];
      }
      // return data;
      const c = this.list.find((cl) => cl.id === classId);
      if (c) c.stuNum += data.successNum;
      return true;
    } catch (err) {
      throw new Error(err);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async importStudentDamocles(formData: FormData, classId: string) {
    try {
      const { data: { data: apiData } } = await axios.uploadStudents(formData, classId);
      return apiData;
    } catch (error) {
      throw new Error(error);
    }
  }

  async update(reqData: { name: string; teacherCodes: string; id: string }): Promise<ClassInfoI> {
    try {
      const data = await editClassData(reqData);
      const classItem = this.list.find((item) => item.id === data.id);
      if (classItem) {
        classItem.name = data.name || '';
        classItem.teacherCodes = data.teacherCodes || '';
      }
      return classItem as ClassInfoI;
    } catch (err) {
      throw new Error(err);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async create(reqData: { name: string; teacherCodes: string }): Promise<ClassInfoI> {
    try {
      const data = await editClassData(reqData);
      const newItem = {
        name: data.name || '',
        id: data.id || '',
        stuNum: data.stuNum || 0,
        teacherCodes: data.teacherCodes || '',
        teacherName: data.userNames || '',
      };
      // this.list.splice(0, 1, newItem);
      return newItem;
    } catch (err) {
      throw new Error(err);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(reqData: { id: string; name: string; disable: boolean }): Promise<boolean> {
    try {
      await editClassData(reqData);
      return true;
    } catch (err) {
      throw new Error(err);
    }
  }

  async query(reqData: { pageSize: number; pageNo: number }): Promise<ClassInfoI[]> {
    const { classList, totalRecords } = await getClassList(reqData);
    this.list = classList;
    this.totalRecords = totalRecords || 0;
    return this.list;
  }

  async getTeachers(pn?: number, ps?: number) {
    const list = await (await TeacherModuleFun()).queryTeacher(pn, ps);
    this.teacherList = list;
  }
}

const editFormRules = {
  name: [
    // { reg: /^[A-Za-z\u4E00-\u9FA5_-]+$/, message: '存在异常字符，请重新输入！' },
    { max: 50, min: 1, message: '班级名称字数限制为1-50个汉字' },
  ],
  teacherCodes: [
    { required: true, message: '请选择班级教师' },
  ],
};

const basicEditForm: ClassEditFormI = {
  name: '',
  teacherCodes: '',
};

export default (Message: MessageApi = message) => {
  const pagination = reactive({
    pageSize: 12,
    pageNo: 1,
  });

  const modalVisible = reactive({
    import: false,
    edit: false,
    delete: false,
    plus: false,
  });

  const modalConfirmLoading = reactive({
    import: false,
    edit: false,
    delete: false,
    plus: false,
  });

  const editForm: ClassEditFormI = reactive({ ...basicEditForm });

  const importForm = reactive({
    file: [] as FileItem[],
  });

  const teacherSelectDisabled = CommonStore.getState().role !== 'admin';

  const userId = CommonStore.getState().code;

  let currentClass: ClassInfoI;

  const classModule = reactive(new Class());

  const editFormRef = ref();

  // onMounted(async () => {
  //   await classModule.query(pagination);
  // });

  const handleClick = async (showValidate: Ref<boolean>, classItem: ClassInfoI, classMemberModule: ClassMember) => {
    classMemberModule.setClassName(classItem.name);
    classMemberModule.setClassId(classItem.id);
    classMemberModule.setClass(classItem);
    await classMemberModule.query({
      pageSize: 12,
      pageNo: 1,
    });
    showValidate.value = true;
  };

  const handlePageChange = async () => {
    await classModule.query(pagination);
  };

  const handleImportClick = async (c: ClassInfoI) => {
    // classModule.importStudent();
    currentClass = c;
    modalVisible.import = true;
  };

  const handleEditClick = async (c: ClassInfoI) => {
    if (classModule.teacherList.length === 0) {
      await classModule.getTeachers(1, 999);
    }
    editForm.name = c.name;
    editForm.teacherCodes = c.teacherCodes;
    currentClass = c;
    modalVisible.edit = true;
  };

  const handleDeleteClick = async (c: ClassInfoI) => {
    // classModule
    promiseConfirm('将删除班级下的学生账号及所有实验数据，此操作不可逆，是否确认删除？', async () => {
      // modalConfirmLoading.delete = true;
      await delClassById(c.id);
      Message.success('删除成功!');
      await classModule.query(pagination);
    });
    // currentClass = c;
    // modalVisible.delete = true;
  };

  const handlePlusClick = async () => {
    if (classModule.teacherList.length === 0) {
      await classModule.getTeachers(1, 999);
    }
    if (teacherSelectDisabled) editForm.teacherCodes = userId;
    modalVisible.plus = true;
  };

  const handleImportCancel = () => {
    modalVisible.import = false;
    importForm.file = [];
  };

  const handleEditCancel = () => {
    modalVisible.edit = false;
    Object.assign(editForm, basicEditForm);
  };

  const handleDeleteCancel = async () => {
    modalVisible.delete = false;
  };

  const handlePlusCancel = () => {
    modalVisible.plus = false;
    editForm.name = '';
    editForm.teacherCodes = '';
  };

  const handleImportOk = async () => {
    if (importForm.file.length === 0) {
      Message.warning('请导入学生名单!');
      return;
    }
    const file = importForm.file[0];
    if (file.type && file.type !== 'application/vnd.ms-excel') {
      importForm.file = [];
      Message.warning('请选择正确的文件格式');
      return;
    }
    // return;
    try {
      modalConfirmLoading.import = true;
      const formData = new FormData();
      formData.append('file', file.originFileObj);
      // const data = await classModule.importStudent(formData, currentClass.id);
      const data = await classModule.importStudentDamocles(formData, currentClass.id);
      console.log(data, 'data');
      if (Array.isArray(data) && data.length > 0) {
        // Message.warning(`
        // ${data}
        // `);
        Modal.warn({
          title: '信息导入异常',
          content: '请下载异常数据表格，修改完成后重新导入异常数据表格',
          okText: '下载表格',
          onOk: () => {
            exportErrorExcel(data);
          },
        });
      } else {
        Message.success('导入成功');
        classModule.query(pagination);
      }
      handleImportCancel();
      // }
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.import = false;
    }
  };

  const handleEditOk = async () => {
    try {
      validate(editForm, editFormRules);
      modalConfirmLoading.edit = true;
      await classModule.update({
        id: currentClass.id,
        ...editForm,
      });
      await classModule.query(pagination);
      handleEditCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.edit = false;
    }
  };

  const handleDeleteOk = async () => {
    try {
      modalConfirmLoading.delete = true;
      // Message.info('删除功能暂未开放');
      handleDeleteCancel();
      await delClassById(currentClass.id);
      Message.success('删除成功!');
      await classModule.query(pagination);
      handleDeleteCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.delete = false;
    }
  };

  const handlePlusOk = async () => {
    try {
      validate(editForm, editFormRules);
      modalConfirmLoading.plus = true;
      await classModule.create(editForm);
      message.success('添加班级成功');
      handlePlusCancel();
      await handlePageChange();
      modalConfirmLoading.plus = false;
      return true;
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.plus = false;
    }
  };

  const handleImportBeforeUpload = (file: FileItem) => {
    importForm.file = [file];
  };

  const handleImportRemove = () => {
    importForm.file = [];
  };

  return {
    teacherSelectDisabled,
    userId,
    pagination,
    modalVisible,
    modalConfirmLoading,
    importForm,
    editForm,
    editFormRef,
    editFormRules,
    classModule,
    handleClick,
    handlePageChange,
    handleImportClick,
    handlePlusClick,
    handleEditClick,
    handleDeleteClick,
    handleImportCancel,
    handleEditCancel,
    handleDeleteCancel,
    handlePlusCancel,
    handleImportOk,
    handleEditOk,
    handleDeleteOk,
    handlePlusOk,
    handleImportBeforeUpload,
    handleImportRemove,
  };
};
